import React, { useState, useEffect } from 'react';
import { 
  Crown, Users, DollarSign, CheckCircle, XCircle, RefreshCw, Clock,
  Phone, Mail, User, Calendar, CreditCard, Package, Search, Eye,
  AlertCircle, X, Trash2, Filter, Download, TrendingUp, Loader2,
  Facebook, Check, Settings, Archive, Bell, BarChart3, Activity,
  Zap, Target, Award, Shield, Info, ChevronRight, Copy, ExternalLink
} from 'lucide-react';
import { API_URL } from '../../config';

const styles = `
  @keyframes slideUp {
    from { transform: translateY(100%); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  .animate-slide-up { animation: slideUp 0.3s ease-out; }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .animate-fade-in { animation: fadeIn 0.2s ease-out; }
  
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .animate-shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(to right, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%);
    background-size: 1000px 100%;
  }
  
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  .animate-bounce-slow {
    animation: bounce 3s ease-in-out infinite;
  }
  
  @keyframes pulse-ring {
    0% { transform: scale(1); opacity: 1; }
    100% { transform: scale(1.5); opacity: 0; }
  }
  .animate-pulse-ring {
    animation: pulse-ring 2s ease-out infinite;
  }
  
  /* Custom scrollbar */
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.5);
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 92, 246, 0.8);
  }
`;

export default function AdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSub, setSelectedSub] = useState(null);
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('date_desc');
  const [confirmModal, setConfirmModal] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openConfirmModal = (title, message, onConfirm, type = 'info') => {
    setConfirmModal({ title, message, onConfirm, type });
  };

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/subscriptions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      } else {
        showToast('Erreur de chargement', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showToast('Erreur de connexion', 'error');
    } finally {
      setLoading(false);
    }
  };

  const validateSubscription = async (subId) => {
    openConfirmModal(
      '✅ Valider l\'abonnement',
      'Activer cet abonnement et le rendre accessible au client ?',
      async () => {
        setActionLoading(true);
        try {
          const response = await fetch(`${API_URL}/api/admin/subscriptions/${subId}/validate`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            showToast('✅ Abonnement activé avec succès !', 'success');
            loadSubscriptions();
            setSelectedSub(null);
          } else {
            const error = await response.json();
            showToast(error.message || 'Erreur de validation', 'error');
          }
        } catch (error) {
          showToast('Erreur de connexion', 'error');
        } finally {
          setActionLoading(false);
        }
      },
      'success'
    );
  };

  const toggleStatus = async (subId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'expired' : 'active';
    const action = newStatus === 'active' ? 'activer' : 'désactiver';
    
    openConfirmModal(
      `${action.charAt(0).toUpperCase() + action.slice(1)} l'abonnement`,
      `Êtes-vous sûr de vouloir ${action} cet abonnement ?`,
      async () => {
        setActionLoading(true);
        try {
          const response = await fetch(`${API_URL}/api/admin/subscriptions/${subId}/status`, {
            method: 'PUT',
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status: newStatus })
          });
          
          if (response.ok) {
            showToast(`Abonnement ${newStatus === 'active' ? 'activé' : 'désactivé'}`, 'success');
            loadSubscriptions();
            setSelectedSub(null);
          } else {
            showToast('Erreur lors du changement de statut', 'error');
          }
        } catch (error) {
          showToast('Erreur de connexion', 'error');
        } finally {
          setActionLoading(false);
        }
      },
      newStatus === 'active' ? 'success' : 'warning'
    );
  };

  const cancelSubscription = async (subId) => {
    openConfirmModal(
      '⚠️ Annuler l\'abonnement',
      'Cette action annulera définitivement cet abonnement. Continuer ?',
      async () => {
        setActionLoading(true);
        try {
          const response = await fetch(`${API_URL}/api/admin/subscriptions/${subId}/cancel`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            showToast('Abonnement annulé', 'success');
            loadSubscriptions();
            setSelectedSub(null);
          } else {
            showToast('Erreur lors de l\'annulation', 'error');
          }
        } catch (error) {
          showToast('Erreur de connexion', 'error');
        } finally {
          setActionLoading(false);
        }
      },
      'danger'
    );
  };

  const deleteSubscription = async (subId) => {
    openConfirmModal(
      '🗑️ ATTENTION : Suppression définitive',
      'Cette action supprimera définitivement cet abonnement de la base de données. Cette action est IRRÉVERSIBLE. Êtes-vous absolument sûr ?',
      async () => {
        setActionLoading(true);
        try {
          const response = await fetch(`${API_URL}/api/admin/subscriptions/${subId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
          });
          
          if (response.ok) {
            showToast('Abonnement supprimé', 'success');
            loadSubscriptions();
            setSelectedSub(null);
          } else {
            showToast('Erreur lors de la suppression', 'error');
          }
        } catch (error) {
          showToast('Erreur de connexion', 'error');
        } finally {
          setActionLoading(false);
        }
      },
      'danger'
    );
  };

  const filteredSubs = subscriptions.filter(sub => {
    if (filter === 'pending' && sub.status !== 'pending_setup') return false;
    if (filter === 'active' && sub.status !== 'active') return false;
    if (filter === 'expired' && sub.status !== 'expired') return false;
    if (filter === 'cancelled' && sub.status !== 'cancelled') return false;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        sub.user_name?.toLowerCase().includes(search) ||
        sub.user_email?.toLowerCase().includes(search) ||
        sub.service_name?.toLowerCase().includes(search) ||
        sub.phone_number?.toLowerCase().includes(search) ||
        sub.transaction_id?.toLowerCase().includes(search)
      );
    }
    
    return true;
  }).sort((a, b) => {
    if (sortBy === 'date_desc') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'date_asc') return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === 'amount_desc') return (b.amount_paid || 0) - (a.amount_paid || 0);
    if (sortBy === 'amount_asc') return (a.amount_paid || 0) - (b.amount_paid || 0);
    if (sortBy === 'name_asc') return (a.user_name || '').localeCompare(b.user_name || '');
    return 0;
  });

  const stats = {
    total: subscriptions.length,
    pending: subscriptions.filter(s => s.status === 'pending_setup').length,
    active: subscriptions.filter(s => s.status === 'active').length,
    expired: subscriptions.filter(s => s.status === 'expired').length,
    cancelled: subscriptions.filter(s => s.status === 'cancelled').length,
    revenue: subscriptions
      .filter(s => s.status === 'active')
      .reduce((sum, s) => sum + (s.amount_paid || s.price || 0), 0),
    total_revenue: subscriptions
      .reduce((sum, s) => sum + (s.amount_paid || s.price || 0), 0)
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return '0';
    return new Intl.NumberFormat('fr-MG').format(amount);
  };

  const getStatusBadge = (status) => {
    const configs = {
      'pending_setup': {
        bg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
        text: 'text-white',
        icon: Clock,
        label: 'En attente',
        pulse: true
      },
      'active': {
        bg: 'bg-gradient-to-r from-green-500 to-emerald-500',
        text: 'text-white',
        icon: CheckCircle,
        label: 'Actif',
        pulse: true
      },
      'expired': {
        bg: 'bg-gradient-to-r from-gray-500 to-slate-500',
        text: 'text-white',
        icon: XCircle,
        label: 'Expiré',
        pulse: false
      },
      'cancelled': {
        bg: 'bg-gradient-to-r from-red-500 to-rose-500',
        text: 'text-white',
        icon: AlertCircle,
        label: 'Annulé',
        pulse: false
      }
    };

    const config = configs[status] || configs['expired'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold ${config.bg} ${config.text} shadow-lg`}>
        <Icon className={`w-4 h-4 ${config.pulse ? 'animate-pulse' : ''}`} />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="relative">
          <Loader2 className="w-16 h-16 animate-spin text-purple-500 mb-4" />
          <div className="absolute inset-0 w-16 h-16 rounded-full bg-purple-500/20 animate-pulse-ring"></div>
        </div>
        <p className="text-white/80 text-lg font-medium">Chargement des abonnements...</p>
        <p className="text-white/50 text-sm mt-2">Veuillez patienter</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
      <style>{styles}</style>
      
      {/* 🎯 HEADER STICKY */}
      <div className="flex-none bg-gradient-to-br from-slate-900/95 via-purple-900/95 to-slate-900/95 backdrop-blur-xl border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
          {/* Header avec titre et boutons */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-2xl animate-bounce-slow">
                <Crown className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black text-white mb-1 flex items-center gap-2">
                  Gestion Abonnements
                  {stats.pending > 0 && (
                    <span className="relative">
                      <Bell className="w-6 h-6 text-yellow-400 animate-bounce" />
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                        {stats.pending}
                      </span>
                    </span>
                  )}
                </h1>
                <p className="text-white/70 text-base md:text-lg flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Suivi en temps réel • {filteredSubs.length} résultat(s)
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 backdrop-blur-xl border rounded-xl text-white transition-all flex items-center gap-2 ${
                  showFilters 
                    ? 'bg-purple-600 border-purple-500' 
                    : 'bg-white/10 hover:bg-white/20 border-white/20'
                }`}
              >
                <Filter className="w-5 h-5" />
                <span className="hidden md:inline">Filtres</span>
              </button>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-xl text-white transition-all appearance-none cursor-pointer"
              >
                <option value="date_desc" className="bg-slate-900">Plus récents</option>
                <option value="date_asc" className="bg-slate-900">Plus anciens</option>
                <option value="amount_desc" className="bg-slate-900">Montant ↓</option>
                <option value="amount_asc" className="bg-slate-900">Montant ↑</option>
                <option value="name_asc" className="bg-slate-900">Nom A-Z</option>
              </select>
              
              <button
                onClick={loadSubscriptions}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 rounded-xl text-white font-bold transition-all flex items-center gap-2 shadow-lg hover:shadow-purple-500/50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline">Actualiser</span>
              </button>
            </div>
          </div>

          {/* 📊 STATS */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard title="Total" value={stats.total} icon={Package} gradient="from-blue-600 to-cyan-600" iconColor="text-blue-100" />
            <StatCard title="En attente" value={stats.pending} icon={Clock} gradient="from-yellow-600 to-orange-600" iconColor="text-yellow-100" pulse={stats.pending > 0} highlight={stats.pending > 0} />
            <StatCard title="Actifs" value={stats.active} icon={CheckCircle} gradient="from-green-600 to-emerald-600" iconColor="text-green-100" />
            <StatCard title="Expirés" value={stats.expired} icon={XCircle} gradient="from-gray-600 to-slate-600" iconColor="text-gray-100" />
            <StatCard title="Annulés" value={stats.cancelled} icon={AlertCircle} gradient="from-red-600 to-rose-600" iconColor="text-red-100" />
            <StatCard title="Revenus (K)" value={(stats.revenue / 1000).toFixed(0)} subtitle={`Total: ${(stats.total_revenue / 1000).toFixed(0)}K`} icon={DollarSign} gradient="from-purple-600 to-pink-600" iconColor="text-purple-100" />
          </div>

          {/* 🔍 RECHERCHE & FILTRES */}
          <div className="space-y-3">
            {/* Recherche */}
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 group-focus-within:text-purple-400 transition-colors" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="🔍 Rechercher par nom, email, service, téléphone, transaction..."
                className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-500 focus:bg-white/15 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-lg transition-all"
                >
                  <X className="w-4 h-4 text-white/60" />
                </button>
              )}
            </div>

            {/* Filtres tags */}
            {showFilters && (
              <div className="flex flex-wrap gap-2 animate-fade-in">
                {[
                  { id: 'all', label: 'Tous', count: stats.total, icon: Package },
                  { id: 'pending', label: 'En attente', count: stats.pending, icon: Clock },
                  { id: 'active', label: 'Actifs', count: stats.active, icon: CheckCircle },
                  { id: 'expired', label: 'Expirés', count: stats.expired, icon: XCircle },
                  { id: 'cancelled', label: 'Annulés', count: stats.cancelled, icon: AlertCircle }
                ].map(f => {
                  const Icon = f.icon;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFilter(f.id)}
                      className={`px-4 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                        filter === f.id
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/50 scale-105'
                          : 'bg-white/10 text-white/70 hover:bg-white/20 hover:scale-105'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{f.label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        filter === f.id ? 'bg-white/30' : 'bg-white/20'
                      }`}>
                        {f.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 📋 LISTE SCROLLABLE */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          {filteredSubs.length === 0 ? (
            <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-12 text-center shadow-xl">
              <div className="relative inline-block mb-6">
                <Package className="w-24 h-24 text-white/20 mx-auto" />
                <Search className="w-10 h-10 text-white/30 absolute -bottom-2 -right-2" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Aucun abonnement trouvé</h3>
              <p className="text-white/60 mb-6">
                {searchTerm 
                  ? `Aucun résultat pour "${searchTerm}"`
                  : filter !== 'all'
                    ? 'Aucun abonnement dans cette catégorie'
                    : 'Aucun abonnement à afficher'}
              </p>
              {(searchTerm || filter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilter('all');
                  }}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-xl text-white font-bold transition-all"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredSubs.map(sub => (
                <SubscriptionCard
                  key={sub.$id}
                  subscription={sub}
                  onViewDetails={() => setSelectedSub(sub)}
                  getStatusBadge={getStatusBadge}
                  formatDate={formatDate}
                  formatAmount={formatAmount}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MODALS & TOASTS */}
      {selectedSub && (
        <DetailsModal
          subscription={selectedSub}
          onClose={() => setSelectedSub(null)}
          onValidate={() => validateSubscription(selectedSub.$id)}
          onToggleStatus={() => toggleStatus(selectedSub.$id, selectedSub.status)}
          onCancel={() => cancelSubscription(selectedSub.$id)}
          onDelete={() => deleteSubscription(selectedSub.$id)}
          actionLoading={actionLoading}
          getStatusBadge={getStatusBadge}
          formatDate={formatDate}
          formatAmount={formatAmount}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          onConfirm={() => {
            confirmModal.onConfirm();
            setConfirmModal(null);
          }}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
          <div className={`px-6 py-4 rounded-2xl shadow-2xl backdrop-blur-xl border-2 flex items-center gap-3 ${
            toast.type === 'success' 
              ? 'bg-green-500/20 border-green-500 text-green-100'
              : 'bg-red-500/20 border-red-500 text-red-100'
          }`}>
            {toast.type === 'success' ? (
              <div className="relative">
                <CheckCircle className="w-6 h-6" />
                <div className="absolute inset-0 w-6 h-6 rounded-full bg-green-400/20 animate-pulse-ring"></div>
              </div>
            ) : (
              <XCircle className="w-6 h-6" />
            )}
            <span className="font-bold text-lg">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Composants (StatCard, SubscriptionCard, DetailsModal, ConfirmModal, etc.) - identiques à avant
// Je les garde pour que le fichier soit complet

function StatCard({ title, value, subtitle, icon: Icon, gradient, iconColor, pulse, highlight }) {
  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-xl p-3 md:p-4 shadow-xl hover:scale-105 transition-transform ${highlight ? 'ring-2 ring-yellow-400 animate-pulse' : ''}`}>
      <div className="flex items-center justify-between mb-1">
        <Icon className={`w-6 md:w-8 h-6 md:h-8 ${iconColor} ${pulse ? 'animate-pulse' : ''}`} />
        <div className="text-2xl md:text-3xl font-black text-white">{value}</div>
      </div>
      <p className="text-white/90 font-semibold text-xs md:text-sm">{title}</p>
      {subtitle && <p className="text-white/60 text-xs mt-0.5">{subtitle}</p>}
    </div>
  );
}

function SubscriptionCard({ subscription, onViewDetails, getStatusBadge, formatDate, formatAmount }) {
  const getServiceIcon = (serviceId) => {
    const icons = {
      'teckia_vtryon': '👗',
      'video_ads': '🎬',
      'teckia_video_ads': '🎬',
      'marketing_studio': '🎨',
      'ai_agent_alone': '🤖',
      'ai_agent_website': '🌐',
      'whatsapp_bot': '💬',
      'messenger_bot': '📱'
    };
    return icons[serviceId] || '📦';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div 
    onClick={onViewDetails}
    className="group bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-5 hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/20 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform">
            {getServiceIcon(subscription.service_id)}
          </div>
          <div>
            <h3 className="text-lg font-bold text-white group-hover:text-purple-300 transition-colors flex items-center gap-2">
              {subscription.service_name || subscription.service_id}
              <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            </h3>
            <p className="text-sm text-white/60">{subscription.user_name || 'N/A'}</p>
          </div>
        </div>
        {getStatusBadge(subscription.status)}
      </div>

      <div className="space-y-2 mb-4">
        <InfoRow icon={Mail} label={subscription.user_email || 'N/A'} />
        {subscription.phone_number && <InfoRow icon={Phone} label={subscription.phone_number} />}
        <InfoRow icon={Calendar} label={formatDate(subscription.created_at)} />
        <InfoRow icon={CreditCard} label={subscription.pricing_type === 'monthly' ? 'Mensuel' : subscription.pricing_type === 'credits' ? 'Crédits' : subscription.pricing_type || 'N/A'} />
        {(subscription.amount_paid || subscription.price) && <InfoRow icon={DollarSign} label={`${formatAmount(subscription.amount_paid)} Ar`} valueClass="text-green-400 font-bold" />}
        {subscription.transaction_id && (
          <div className="flex items-center gap-2 text-xs text-white/40">
            <Package className="w-3 h-3" />
            <span className="font-mono truncate">{subscription.transaction_id.substring(0, 20)}...</span>
            <button onClick={() => copyToClipboard(subscription.transaction_id)} className="p-1 hover:bg-white/10 rounded transition-all" title="Copier">
              <Copy className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails();
        }}
      >
        <Eye className="w-5 h-5" />
        Voir détails complets
        <ExternalLink className="w-4 h-4" />
      </button>
    </div>
  );
}

function InfoRow({ icon: Icon, label, valueClass = "text-white/80" }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4 text-white/40" />
      <span className={valueClass}>{label}</span>
    </div>
  );
}

function ConfirmModal({ title, message, type = 'info', onConfirm, onCancel }) {
  const configs = {
    success: { gradient: 'from-green-600 to-emerald-600', icon: CheckCircle },
    warning: { gradient: 'from-orange-600 to-red-600', icon: AlertCircle },
    danger: { gradient: 'from-red-600 to-rose-600', icon: XCircle },
    info: { gradient: 'from-blue-600 to-cyan-600', icon: Info }
  };

  const config = configs[type] || configs.info;
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-fade-in" onClick={onCancel}>
      <div className="bg-gradient-to-br from-slate-900 to-purple-900 border-2 border-purple-500/50 rounded-3xl max-w-md w-full shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className={`bg-gradient-to-r ${config.gradient} p-6 rounded-t-3xl`}>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-xl">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white">{title}</h3>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-white/80 text-lg leading-relaxed">{message}</p>

          <div className="flex gap-3">
            <button onClick={onCancel} className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white font-bold transition-all">
              Annuler
            </button>
            <button onClick={onConfirm} className={`flex-1 px-6 py-3 bg-gradient-to-r ${config.gradient} hover:opacity-90 rounded-xl text-white font-bold transition-all flex items-center justify-center gap-2 shadow-lg`}>
              <Icon className="w-5 h-5" />
              Confirmer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailsModal({ subscription, onClose, onValidate, onToggleStatus, onCancel, onDelete, actionLoading, getStatusBadge, formatDate, formatAmount }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in overflow-y-auto" onClick={onClose}>
      <div className="bg-gradient-to-br from-slate-900 to-purple-900 border-2 border-purple-500/50 rounded-3xl max-w-4xl w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-6 flex items-center justify-between border-b border-white/20 rounded-t-3xl z-10">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Package className="w-7 h-7" />
            </div>
            Détails Abonnement
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-all">
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar">
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
            <span className="text-white/70 font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Statut actuel
            </span>
            {getStatusBadge(subscription.status)}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Section title="Informations Client" icon={User} iconColor="text-blue-400">
              <InfoItem label="Nom" value={subscription.user_name || 'N/A'} />
              <InfoItem label="Email" value={subscription.user_email || 'N/A'} icon={Mail} />
              {subscription.phone_number && <InfoItem label="Téléphone" value={subscription.phone_number} icon={Phone} />}
              <InfoItem label="User ID" value={subscription.user_id} valueClass="font-mono text-xs" />
            </Section>

            <Section title="Service & Plan" icon={Package} iconColor="text-purple-400">
              <InfoItem label="Service" value={subscription.service_name || subscription.service_id} />
              <InfoItem label="Type" value={subscription.pricing_type === 'monthly' ? '📅 Mensuel' : subscription.pricing_type === 'credits' ? '🎫 Crédits' : subscription.pricing_type || 'N/A'} />
              {subscription.credits_quantity && <InfoItem label="Quantité" value={`${subscription.credits_quantity} crédits`} icon={Award} />}
              {subscription.facebook_page_id && <InfoItem label="Page Facebook" value={subscription.facebook_page_name || subscription.facebook_page_id} icon={Facebook} />}
            </Section>
          </div>

          <Section title="Paiement" icon={CreditCard} iconColor="text-green-400">
            <div className="grid md:grid-cols-3 gap-4">
              <InfoItem label="Montant payé" value={subscription.amount_paid ? `${formatAmount(subscription.amount_paid)} Ar` : subscription.price ? `${formatAmount(subscription.price)} Ar` : 'N/A'} valueClass="text-green-400 font-bold text-xl" />
              <InfoItem label="Méthode" value={(subscription.payment_provider || 'N/A').toUpperCase()} valueClass="font-semibold" />
              <InfoItem label="Order ID" value={subscription.order_id || 'N/A'} valueClass="font-mono text-xs" />
            </div>
            {subscription.transaction_id && <InfoItem label="Transaction ID" value={subscription.transaction_id} valueClass="font-mono text-xs break-all" fullWidth />}
          </Section>

          <Section title="Chronologie" icon={Calendar} iconColor="text-yellow-400">
            <div className="grid md:grid-cols-2 gap-4">
              <InfoItem label="📅 Créé le" value={formatDate(subscription.created_at)} />
              {subscription.starts_at && <InfoItem label="▶️ Début" value={formatDate(subscription.starts_at)} />}
              {subscription.ends_at && <InfoItem label="⏹️ Fin" value={formatDate(subscription.ends_at)} />}
              {subscription.activated_at && <InfoItem label="✅ Activé le" value={formatDate(subscription.activated_at)} />}
            </div>
          </Section>

          <Section title="Actions disponibles" icon={Settings} iconColor="text-orange-400">
            <div className="grid md:grid-cols-2 gap-3">
              {subscription.status === 'pending_setup' && <ActionButton onClick={onValidate} disabled={actionLoading} gradient="from-green-600 to-emerald-600" icon={CheckCircle} label="✅ Valider & Activer" />}
              {(subscription.status === 'active' || subscription.status === 'expired') && <ActionButton onClick={onToggleStatus} disabled={actionLoading} gradient={subscription.status === 'active' ? 'from-orange-600 to-red-600' : 'from-green-600 to-emerald-600'} icon={subscription.status === 'active' ? XCircle : CheckCircle} label={subscription.status === 'active' ? '⏸️ Désactiver' : '▶️ Activer'} />}
              {subscription.status !== 'cancelled' && <ActionButton onClick={onCancel} disabled={actionLoading} gradient="from-red-600 to-rose-600" icon={AlertCircle} label="❌ Annuler l'abonnement" />}
              <ActionButton onClick={onDelete} disabled={actionLoading} gradient="from-gray-700 to-slate-800" icon={Trash2} label="🗑️ Supprimer définitivement" fullWidth />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, iconColor, children }) {
  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-5 space-y-4">
      <h3 className="font-bold text-white text-lg flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function InfoItem({ label, value, icon: Icon, valueClass = "text-white font-medium", fullWidth = false }) {
  return (
    <div className={fullWidth ? 'col-span-full' : ''}>
      <div className="text-white/60 text-sm mb-1 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={valueClass}>{value}</div>
    </div>
  );
}

function ActionButton({ onClick, disabled, gradient, icon: Icon, label, fullWidth = false }) {
  return (
    <button onClick={onClick} disabled={disabled} className={`px-4 py-3 bg-gradient-to-r ${gradient} hover:opacity-90 disabled:opacity-50 rounded-xl text-white font-bold transition-all flex items-center justify-center gap-2 shadow-lg hover:scale-105 ${fullWidth ? 'col-span-full' : ''}`}>
      {disabled ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
      {label}
    </button>
  );
}